import React from 'react';
import { TemperatureStats } from '../types';
import { Sunrise, Sun, Moon } from 'lucide-react';

interface TemperatureTableProps {
  stats: TemperatureStats;
}

const TemperatureTable: React.FC<TemperatureTableProps> = ({ stats }) => {
  const formatTemperature = (temp: number) => `${temp.toFixed(1)}°C`;

  const getTemperatureColor = (temp: number) => {
    if (temp < 6) return 'text-temp-sehrKalt';
    if (temp < 11) return 'text-temp-kalt';
    if (temp < 15) return 'text-temp-kuhl';
    if (temp < 19) return 'text-temp-leichtKuhl';
    if (temp < 22) return 'text-gray-700'; // Use dark gray instead of white for readability
    if (temp < 26) return 'text-temp-leichtWarm';
    if (temp < 32) return 'text-temp-warm';
    if (temp < 37) return 'text-temp-heiss';
    return 'text-temp-sehrHeiss';
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Temperatur</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-gray-200">
            <th className="text-left py-3 px-4 font-semibold text-gray-700">Temperatur</th>
            <th className="text-center py-3 px-4 font-semibold text-gray-700">
              <div className="flex items-center justify-center space-x-2">
                <Sunrise className="w-4 h-4" stroke="#6ABD89" />
                <span>Morgens</span>
              </div>
            </th>
            <th className="text-center py-3 px-4 font-semibold text-gray-700">
              <div className="flex items-center justify-center space-x-2">
                <Sun className="w-4 h-4" stroke="#6ABD89" />
                <span>Mittags</span>
              </div>
            </th>
            <th className="text-center py-3 px-4 font-semibold text-gray-700">
              <div className="flex items-center justify-center space-x-2">
                <Moon className="w-4 h-4" stroke="#6ABD89" />
                <span>Nachmittags/Abends</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-gray-100 hover:bg-gray-50">
            <td className="py-3 px-4 font-medium text-gray-800">Gemessene Durchschnitts Temp.</td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.measured.morning)}`}>
              {formatTemperature(stats.measured.morning)}
            </td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.measured.midday)}`}>
              {formatTemperature(stats.measured.midday)}
            </td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.measured.afternoon)}`}>
              {formatTemperature(stats.measured.afternoon)}
            </td>
          </tr>
          <tr className="border-b border-gray-100 hover:bg-gray-50">
            <td className="py-3 px-4 font-medium text-gray-800">Höchste gemessene Temperatur</td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.maxMeasured.morning)}`}>
              {formatTemperature(stats.maxMeasured.morning)}
            </td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.maxMeasured.midday)}`}>
              {formatTemperature(stats.maxMeasured.midday)}
            </td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.maxMeasured.afternoon)}`}>
              {formatTemperature(stats.maxMeasured.afternoon)}
            </td>
          </tr>
          <tr className="border-b border-gray-100 hover:bg-gray-50">
            <td className="py-3 px-4 font-medium text-gray-800">Niedrigste gemessene Temperatur</td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.minMeasured.morning)}`}>
              {formatTemperature(stats.minMeasured.morning)}
            </td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.minMeasured.midday)}`}>
              {formatTemperature(stats.minMeasured.midday)}
            </td>
            <td className={`py-3 px-4 text-center font-semibold ${getTemperatureColor(stats.minMeasured.afternoon)}`}>
              {formatTemperature(stats.minMeasured.afternoon)}
            </td>
          </tr>
        </tbody>
      </table>
        </div>
        
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Hinweis:</strong> Diese Daten repräsentieren die gefilterten Messungen basierend auf den ausgewählten Kriterien.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TemperatureTable;
